import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Package
} from 'lucide-react';
import { ProductsService } from '../firebase';
import { Product, defaultProduct, categoryOptions, subcategoryOptions, currencyOptions, weddingDressOptions, eveningDressOptions, hennaDressOptions, sizeOptions } from '../types/product';
import imageCompression from 'browser-image-compression';
import ImageUploadService from '../services/imageUploadService';

const ProductManagementContainer = styled.div`
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #333;
  font-size: 1.8rem;
  font-weight: 300;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: #007bff;
          color: white;
          &:hover { background: #0056b3; }
        `;
      case 'danger':
        return `
          background: #dc3545;
          color: white;
          &:hover { background: #c82333; }
        `;
      default:
        return `
          background: #6c757d;
          color: white;
          &:hover { background: #545b62; }
        `;
    }
  }}
`;

const SearchBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 30px;
  padding: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 14px 18px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-size: 15px;
  transition: all 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #999;
  }
`;

const FilterSelect = styled.select`
  padding: 14px 18px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-size: 15px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }
`;

const ProductImage = styled.div<{ $bgImage: string }>`
  height: 200px;
  background: url(${props => props.$bgImage}) center/cover;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: auto;
  -ms-interpolation-mode: bicubic;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: translateZ(0);
  position: relative;
`;

const ProductBadges = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 5px;
`;

const Badge = styled.span<{ type: 'sale' | 'featured' | 'new' }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;

  ${props => {
    switch (props.type) {
      case 'sale':
        return 'background: #dc3545;';
      case 'featured':
        return 'background: #ffc107; color: #000;';
      case 'new':
        return 'background: #28a745;';
    }
  }}
`;

const ProductContent = styled.div`
  padding: 20px;
`;

const ProductName = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
`;

const ProductDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 15px;
  line-height: 1.4;
`;

const ProductPrice = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;

const CurrentPrice = styled.span<{ $onSale?: boolean }>`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${props => props.$onSale ? '#dc3545' : '#333'};
`;

const OriginalPrice = styled.span`
  font-size: 1rem;
  color: #999;
  text-decoration: line-through;
`;

const DiscountPercent = styled.span`
  background: #dc3545;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const ProductActions = styled.div`
  display: flex;
  gap: 10px;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
`;

const InfoTag = styled.span`
  background: #f8f9fa;
  color: #666;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
`;

const Modal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 99999;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 10px;
  padding: 30px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  background: white;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  cursor: pointer;
`;

const ImagePreview = styled.div<{ $bgImage: string }>`
  width: 100px;
  height: 100px;
  background: url(${props => props.$bgImage}) center/cover;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: auto;
  -ms-interpolation-mode: bicubic;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform: translateZ(0);
  border-radius: 8px;
  margin: 10px;
  position: relative;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  font-size: 12px;
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const SectionTitle = styled.h3`
  grid-column: 1 / -1;
  color: #333;
  margin-bottom: 20px;
  font-size: 1.2rem;
  font-weight: 600;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 50px;
`;

const LoadingIcon = styled.div`
  color: #666;
`;

const LoadingText = styled.p`
  margin-top: 20px;
  color: #666;
`;

const ActiveStatusTag = styled(InfoTag)<{ $isActive: boolean }>`
  color: ${props => props.$isActive ? '#28a745' : '#dc3545'};
`;

const UrlInputContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;

const UrlInput = styled(Input)`
  flex: 1;
`;

const UrlInfoText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 15px;
  line-height: 1.6;
  
  code {
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    font-family: monospace;
    color: #333;
  }
`;

const ProductManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formData, setFormData] = useState<Omit<Product, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>>(defaultProduct);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    setLoading(true);
    const result = await ProductsService.getProducts();
    if (result.success && result.data) {
      setProducts(result.data);
    }
    setLoading(false);
  };

  const handleAddProduct = () => {
    setFormData(defaultProduct);
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setFormData(product);
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleDeleteProduct = async (productId: string) => {
    if (window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      const result = await ProductsService.deleteProduct(productId);
      if (result.success) {
        loadProducts();
      }
    }
  };

  const handleSaveProduct = async () => {
    try {
      if (editingProduct) {
        await ProductsService.updateProduct(editingProduct.id!, formData);
      } else {
        await ProductsService.addProduct({
          ...formData,
          createdBy: 'admin' // Bu gerçek admin ID'si olmalı
        });
      }
      setIsModalOpen(false);
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const [imageUrlInput, setImageUrlInput] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const convertGoogleDriveUrl = (url: string): { success: boolean; url?: string; error?: string } => {
    // Google Drive paylaşım linkini doğru formata dönüştür
    // Birden fazla formatı destekliyoruz
    
    // Klasör linklerini kontrol et
    const folderRegex = /drive\.google\.com\/drive\/.*\/folders\/([a-zA-Z0-9_-]+)/;
    const folderMatch = url.match(folderRegex);
    if (folderMatch) {
      return {
        success: false,
        error: 'Bu bir klasör linki. Lütfen klasör içindeki dosyanın sağ tıklayıp "Bağlantıyı al" seçeneğinden dosya linkini kopyalayın. Format: https://drive.google.com/file/d/FILE_ID/view?usp=sharing'
      };
    }
    
    // Klasör linki alternatif formatı
    const folderRegex2 = /drive\.google\.com\/folders\/([a-zA-Z0-9_-]+)/;
    const folderMatch2 = url.match(folderRegex2);
    if (folderMatch2) {
      return {
        success: false,
        error: 'Bu bir klasör linki. Lütfen klasör içindeki dosyanın sağ tıklayıp "Bağlantıyı al" seçeneğinden dosya linkini kopyalayın. Format: https://drive.google.com/file/d/FILE_ID/view?usp=sharing'
      };
    }
    
    // Format 1: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
    // Format 2: https://drive.google.com/file/d/FILE_ID/view
    // Format 3: https://drive.google.com/open?id=FILE_ID
    // Format 4: https://drive.google.com/uc?export=view&id=FILE_ID (zaten doğru format)
    
    let fileId = '';
    
    // Format 1 ve 2: /file/d/FILE_ID/
    const fileRegex = /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/;
    const fileMatch = url.match(fileRegex);
    if (fileMatch && fileMatch[1]) {
      fileId = fileMatch[1];
    }
    
    // Format 3: /open?id=FILE_ID
    if (!fileId) {
      const openRegex = /drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/;
      const openMatch = url.match(openRegex);
      if (openMatch && openMatch[1]) {
        fileId = openMatch[1];
      }
    }
    
    // Format 4: uc?export=view&id=FILE_ID
    if (!fileId) {
      const ucRegex = /uc\?export=view&id=([a-zA-Z0-9_-]+)/;
      const ucMatch = url.match(ucRegex);
      if (ucMatch && ucMatch[1]) {
        fileId = ucMatch[1];
      }
    }
    
    // Format 5: thumbnail?id=FILE_ID&sz=w1920 (zaten thumbnail formatı)
    if (!fileId) {
      const thumbnailRegex = /thumbnail\?id=([a-zA-Z0-9_-]+)/;
      const thumbnailMatch = url.match(thumbnailRegex);
      if (thumbnailMatch && thumbnailMatch[1]) {
        fileId = thumbnailMatch[1];
      }
    }
    
    if (fileId) {
      // Thumbnail formatını kullan - yüksek kaliteli görüntü için w1920 boyutu
      // Eğer thumbnail çalışmazsa, tarayıcı otomatik olarak alternatif formatı deneyecek
      return {
        success: true,
        url: `https://drive.google.com/thumbnail?id=${fileId}&sz=w1920`
      };
    }
    
    // Eğer zaten thumbnail formatındaysa FILE_ID'yi çıkarıp standart formata çevir
    if (url.includes('thumbnail?id=') && fileId) {
      return {
        success: true,
        url: `https://drive.google.com/thumbnail?id=${fileId}&sz=w1920`
      };
    }
    
    // Google Drive linki değilse normal URL olarak kabul et
    if (url.includes('drive.google.com')) {
      return {
        success: false,
        error: 'Geçersiz Google Drive linki. Lütfen dosya linkini kopyalayın (klasör linki değil). Format: https://drive.google.com/file/d/FILE_ID/view?usp=sharing'
      };
    }
    
    // Diğer URL'ler için başarılı döndür
    return {
      success: true,
      url: url
    };
  };

  const handleAddImageUrl = () => {
    if (imageUrlInput.trim()) {
      // Basic URL validation
      try {
        const validatedUrl = imageUrlInput.trim();
        new URL(validatedUrl);
        
        // Google Drive linkini dönüştür
        const result = convertGoogleDriveUrl(validatedUrl);
        
        if (!result.success) {
          alert(result.error || 'Geçerli bir URL giriniz');
          return;
        }
        
        if (result.url) {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, result.url!]
          }));
          setImageUrlInput('');
        }
      } catch {
        alert('Geçerli bir URL giriniz');
      }
    }
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Handle file upload with compression
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      alert('Lütfen bir resim dosyası seçin!');
      return;
    }

    setUploadingImage(true);

    try {
      // Compression options - Agresif sıkıştırma (Client-side optimization)
      const options = {
        maxSizeMB: 0.5,            // Hedef: 500KB altı (ÇOK ÖNEMLİ!)
        maxWidthOrHeight: 1920,    // 4K resme gerek yok, Full HD yeter
        useWebWorker: true,        // Tarayıcıyı dondurmadan işlem yapar
        fileType: 'image/webp',    // Otomatik WebP'ye çevirir (En hafif format)
        initialQuality: 0.75,      // %75 kalite (gözle görülmez fark)
      };

      console.log('📦 Orijinal dosya boyutu:', (file.size / 1024 / 1024).toFixed(2), 'MB');

      // Compress image
      const compressedFile = await imageCompression(file, options);

      console.log('✅ Sıkıştırılmış dosya boyutu:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');
      console.log('📉 Boyut azalması:', ((1 - compressedFile.size / file.size) * 100).toFixed(1), '%');

      // Sıkıştırılmış dosyayı Cloudflare R2'ye yükle.
      // imageCompression orijinal dosya adını koruduğu için uzantıyı düzeltiyoruz.
      const webpFile = new File(
        [compressedFile],
        `${file.name.replace(/\.[^.]+$/, '')}.webp`,
        { type: 'image/webp' }
      );

      const uploadResult = await ImageUploadService.uploadFile(webpFile);

      if (uploadResult.success && uploadResult.url) {
        const uploadedUrl = uploadResult.url;

        setFormData(prev => ({
          ...prev,
          images: [...prev.images, uploadedUrl]
        }));

        const sizeReduction = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
        alert(
          `✅ Resim optimize edilip yüklendi!\n` +
          `Orijinal: ${(file.size / 1024 / 1024).toFixed(2)} MB\n` +
          `Optimize: ${(compressedFile.size / 1024).toFixed(0)} KB (WebP)\n` +
          `Boyut azalması: %${sizeReduction}`
        );
      } else {
        alert(`❌ Görsel yüklenemedi.\n\n${uploadResult.error || 'Bilinmeyen hata'}`);
      }

      setUploadingImage(false);


    } catch (error) {
      console.error('Resim sıkıştırma hatası:', error);
      alert('Resim yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
      setUploadingImage(false);
    }

    // Reset input
    event.target.value = '';
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.shortDescription.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <ProductManagementContainer>
        <LoadingContainer>
          <LoadingIcon>
            <Package size={50} />
          </LoadingIcon>
          <LoadingText>Ürünler yükleniyor...</LoadingText>
        </LoadingContainer>
      </ProductManagementContainer>
    );
  }

  return (
    <ProductManagementContainer>
      <Header>
        <Title>
          <Package size={30} />
          Ürün Yönetimi
        </Title>
        <Button $variant="primary" onClick={handleAddProduct}>
          <Plus size={20} />
          Yeni Ürün Ekle
        </Button>
      </Header>

      <SearchBar>
        <SearchInput
          type="text"
          placeholder="Ürün ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FilterSelect
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          aria-label="Kategori filtresi"
          title="Kategori filtresi"
        >
          <option value="">Tüm Kategoriler</option>
          {categoryOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </FilterSelect>
      </SearchBar>

      <ProductsGrid>
        {filteredProducts.map(product => (
          <ProductCard key={product.id}>
            <ProductImage $bgImage={product.images[0] || '/placeholder.webp'}>
              <ProductBadges>
                {product.isOnSale && <Badge type="sale">İndirim</Badge>}
                {product.isFeatured && <Badge type="featured">Öne Çıkan</Badge>}
                {new Date().getTime() - product.createdAt.getTime() < 7 * 24 * 60 * 60 * 1000 && (
                  <Badge type="new">Yeni</Badge>
                )}
              </ProductBadges>
            </ProductImage>
            
            <ProductContent>
              <ProductName>{product.name}</ProductName>
              <ProductDescription>{product.shortDescription}</ProductDescription>
              
              <ProductPrice>
                <CurrentPrice $onSale={product.isOnSale}>
                  ₺{product.price.toLocaleString()}
                </CurrentPrice>
                {product.isOnSale && product.originalPrice && (
                  <>
                    <OriginalPrice>₺{product.originalPrice.toLocaleString()}</OriginalPrice>
                    <DiscountPercent>%{product.discount}</DiscountPercent>
                  </>
                )}
              </ProductPrice>

              <ProductInfo>
                <InfoTag>{categoryOptions.find(c => c.value === product.category)?.label}</InfoTag>
                <InfoTag>Stok: {product.stock}</InfoTag>
                <InfoTag>{product.colors.length} Renk</InfoTag>
                <InfoTag>{product.sizes.length} Beden</InfoTag>
                <ActiveStatusTag $isActive={product.isActive}>
                  {product.isActive ? 'Aktif' : 'Pasif'}
                </ActiveStatusTag>
              </ProductInfo>

              <ProductActions>
                <Button onClick={() => handleEditProduct(product)}>
                  <Edit size={16} />
                  Düzenle
                </Button>
                <Button $variant="danger" onClick={() => handleDeleteProduct(product.id!)}>
                  <Trash2 size={16} />
                  Sil
                </Button>
              </ProductActions>
            </ProductContent>
          </ProductCard>
        ))}
      </ProductsGrid>

      <Modal $isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
            </ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              <X size={24} />
            </CloseButton>
          </ModalHeader>

          <FormGrid>
            <FormGroup>
              <Label>Ürün Adı</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ürün adını girin"
              />
            </FormGroup>

            <FormGroup>
              <Label>Kategori</Label>
              <Select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                aria-label="Ürün kategorisi seçin"
                title="Ürün kategorisi seçin"
              >
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Alt Kategori</Label>
              <Select
                value={formData.subcategory || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, subcategory: e.target.value }))}
                aria-label="Alt kategori seçin"
                title="Alt kategori seçin"
              >
                <option value="">Alt kategori seçin</option>
                {subcategoryOptions[formData.category]?.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Fiyat</Label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: Number(e.target.value) }))}
                placeholder="0"
                aria-label="Ürün fiyatı"
              />
            </FormGroup>

            <FormGroup>
              <Label>Para Birimi</Label>
              <Select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                aria-label="Para birimi seçin"
                title="Para birimi seçin"
              >
                {currencyOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Bedenler</Label>
              <CheckboxGroup>
                {sizeOptions.map(size => (
                  <CheckboxLabel key={size.value}>
                    <input
                      type="checkbox"
                      checked={formData.sizes.some(s => s.size === size.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ 
                            ...prev, 
                            sizes: [...prev.sizes, { size: size.value, stock: 0 }] 
                          }));
                        } else {
                          setFormData(prev => ({ 
                            ...prev, 
                            sizes: prev.sizes.filter(s => s.size !== size.value) 
                          }));
                        }
                      }}
                      aria-label={`${size.label} bedeni`}
                    />
                    {size.label}
                  </CheckboxLabel>
                ))}
              </CheckboxGroup>
            </FormGroup>

            <FormGroup>
              <Label>İndirim Aktif</Label>
              <CheckboxGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={formData.isOnSale}
                    onChange={(e) => setFormData(prev => ({ ...prev, isOnSale: e.target.checked }))}
                    aria-label="İndirimde"
                  />
                  İndirimde
                </CheckboxLabel>
              </CheckboxGroup>
            </FormGroup>

            {formData.isOnSale && (
              <>
                <FormGroup>
                  <Label>Orijinal Fiyat (₺)</Label>
                  <Input
                    type="number"
                    value={formData.originalPrice || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, originalPrice: Number(e.target.value) }))}
                    placeholder="0"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>İndirim Yüzdesi (%)</Label>
                  <Input
                    type="number"
                    value={formData.discount || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount: Number(e.target.value) }))}
                    placeholder="0"
                  />
                </FormGroup>
              </>
            )}

            <FormGroup>
              <Label>Stok</Label>
              <Input
                type="number"
                value={formData.stock}
                onChange={(e) => setFormData(prev => ({ ...prev, stock: Number(e.target.value) }))}
                placeholder="0"
                aria-label="Stok miktarı"
              />
            </FormGroup>

            <FormGroup>
              <Label>Durum</Label>
              <CheckboxGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    aria-label="Aktif ürün"
                  />
                  Aktif Ürün
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={formData.isFeatured}
                    onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                    aria-label="Öne çıkan ürün"
                  />
                  Öne Çıkan Ürün
                </CheckboxLabel>
              </CheckboxGroup>
            </FormGroup>
          </FormGrid>

          {/* Kategoriye Özel Alanlar */}
          {formData.category === 'gelinlik' && (
            <FormGrid>
              <SectionTitle>Gelinlik Özel Detayları</SectionTitle>

          <FormGroup>
                <Label>Model Tipi</Label>
                <Select
                  value={formData.weddingDressDetails?.modelType || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    weddingDressDetails: { 
                      ...prev.weddingDressDetails, 
                      modelType: e.target.value,
                      necklineType: prev.weddingDressDetails?.necklineType || '',
                      veilCompatible: prev.weddingDressDetails?.veilCompatible || false,
                      backStyle: prev.weddingDressDetails?.backStyle || '',
                      trainLength: prev.weddingDressDetails?.trainLength || ''
                    } 
                  }))}
                  aria-label="Model tipi seçin"
                  title="Model tipi seçin"
                >
                  <option value="">Model tipi seçin</option>
                  {weddingDressOptions.modelTypes.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Yaka Tipi</Label>
                <Select
                  value={formData.weddingDressDetails?.necklineType || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    weddingDressDetails: { 
                      ...prev.weddingDressDetails, 
                      modelType: prev.weddingDressDetails?.modelType || '',
                      necklineType: e.target.value,
                      veilCompatible: prev.weddingDressDetails?.veilCompatible || false,
                      backStyle: prev.weddingDressDetails?.backStyle || '',
                      trainLength: prev.weddingDressDetails?.trainLength || ''
                    } 
                  }))}
                  aria-label="Yaka tipi seçin"
                  title="Yaka tipi seçin"
                >
                  <option value="">Yaka tipi seçin</option>
                  {weddingDressOptions.necklineTypes.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Duvak Uygun</Label>
                <CheckboxGroup>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={formData.weddingDressDetails?.veilCompatible || false}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        weddingDressDetails: { 
                          ...prev.weddingDressDetails, 
                          modelType: prev.weddingDressDetails?.modelType || '',
                          necklineType: prev.weddingDressDetails?.necklineType || '',
                          veilCompatible: e.target.checked,
                          backStyle: prev.weddingDressDetails?.backStyle || '',
                          trainLength: prev.weddingDressDetails?.trainLength || ''
                        } 
                      }))}
                      aria-label="Duvak uygun"
                    />
                    Duvak uygun
                  </CheckboxLabel>
                </CheckboxGroup>
              </FormGroup>

              <FormGroup>
                <Label>Arka Kısım</Label>
                <Select
                  value={formData.weddingDressDetails?.backStyle || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    weddingDressDetails: { 
                      ...prev.weddingDressDetails, 
                      modelType: prev.weddingDressDetails?.modelType || '',
                      necklineType: prev.weddingDressDetails?.necklineType || '',
                      veilCompatible: prev.weddingDressDetails?.veilCompatible || false,
                      backStyle: e.target.value,
                      trainLength: prev.weddingDressDetails?.trainLength || ''
                    } 
                  }))}
                  aria-label="Arka kısım seçin"
                  title="Arka kısım seçin"
                >
                  <option value="">Arka kısım seçin</option>
                  {weddingDressOptions.backStyles.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Kuyruk Uzunluğu</Label>
                <Select
                  value={formData.weddingDressDetails?.trainLength || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    weddingDressDetails: { 
                      ...prev.weddingDressDetails, 
                      modelType: prev.weddingDressDetails?.modelType || '',
                      necklineType: prev.weddingDressDetails?.necklineType || '',
                      veilCompatible: prev.weddingDressDetails?.veilCompatible || false,
                      backStyle: prev.weddingDressDetails?.backStyle || '',
                      trainLength: e.target.value
                    } 
                  }))}
                  aria-label="Kuyruk uzunluğu seçin"
                  title="Kuyruk uzunluğu seçin"
                >
                  <option value="">Kuyruk uzunluğu seçin</option>
                  {weddingDressOptions.trainLengths.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
          )}

          {formData.category === 'abiye' && (
            <FormGrid>
              <SectionTitle>Abiye Özel Detayları</SectionTitle>
              
              <FormGroup>
                <Label>Parıltı Seviyesi</Label>
                <Select
                  value={formData.eveningDressDetails?.sparkleLevel || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    eveningDressDetails: { 
                      ...prev.eveningDressDetails, 
                      sparkleLevel: e.target.value,
                      eventType: prev.eveningDressDetails?.eventType || '',
                      strapType: prev.eveningDressDetails?.strapType || ''
                    } 
                  }))}
                  aria-label="Parıltı seviyesi seçin"
                  title="Parıltı seviyesi seçin"
                >
                  <option value="">Parıltı seviyesi seçin</option>
                  {eveningDressOptions.sparkleLevels.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Etkinlik Türü</Label>
                <Select
                  value={formData.eveningDressDetails?.eventType || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    eveningDressDetails: { 
                      ...prev.eveningDressDetails, 
                      sparkleLevel: prev.eveningDressDetails?.sparkleLevel || '',
                      eventType: e.target.value,
                      strapType: prev.eveningDressDetails?.strapType || ''
                    } 
                  }))}
                  aria-label="Etkinlik türü seçin"
                  title="Etkinlik türü seçin"
                >
                  <option value="">Etkinlik türü seçin</option>
                  {eveningDressOptions.eventTypes.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Askı Tipi</Label>
                <Select
                  value={formData.eveningDressDetails?.strapType || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    eveningDressDetails: { 
                      ...prev.eveningDressDetails, 
                      sparkleLevel: prev.eveningDressDetails?.sparkleLevel || '',
                      eventType: prev.eveningDressDetails?.eventType || '',
                      strapType: e.target.value
                    } 
                  }))}
                  aria-label="Askı tipi seçin"
                  title="Askı tipi seçin"
                >
                  <option value="">Askı tipi seçin</option>
                  {eveningDressOptions.strapTypes.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
          )}

          {formData.category === 'kinalik' && (
            <FormGrid>
              <SectionTitle>Kınalık Özel Detayları</SectionTitle>
              
              <FormGroup>
                <Label>İşleme Türü</Label>
                <Select
                  value={formData.hennaDressDetails?.embroideryType || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    hennaDressDetails: { 
                      ...prev.hennaDressDetails, 
                      embroideryType: e.target.value,
                      accessorySet: prev.hennaDressDetails?.accessorySet || '',
                      colorTheme: prev.hennaDressDetails?.colorTheme || '',
                      kaftanLength: prev.hennaDressDetails?.kaftanLength || ''
                    } 
                  }))}
                  aria-label="İşleme türü seçin"
                  title="İşleme türü seçin"
                >
                  <option value="">İşleme türü seçin</option>
                  {hennaDressOptions.embroideryTypes.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Aksesuar Seti</Label>
                <Select
                  value={formData.hennaDressDetails?.accessorySet || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    hennaDressDetails: { 
                      ...prev.hennaDressDetails, 
                      embroideryType: prev.hennaDressDetails?.embroideryType || '',
                      accessorySet: e.target.value,
                      colorTheme: prev.hennaDressDetails?.colorTheme || '',
                      kaftanLength: prev.hennaDressDetails?.kaftanLength || ''
                    } 
                  }))}
                  aria-label="Aksesuar seti seçin"
                  title="Aksesuar seti seçin"
                >
                  <option value="">Aksesuar seti seçin</option>
                  {hennaDressOptions.accessorySets.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Renk Teması</Label>
                <Select
                  value={formData.hennaDressDetails?.colorTheme || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    hennaDressDetails: { 
                      ...prev.hennaDressDetails, 
                      embroideryType: prev.hennaDressDetails?.embroideryType || '',
                      accessorySet: prev.hennaDressDetails?.accessorySet || '',
                      colorTheme: e.target.value,
                      kaftanLength: prev.hennaDressDetails?.kaftanLength || ''
                    } 
                  }))}
                  aria-label="Renk teması seçin"
                  title="Renk teması seçin"
                >
                  <option value="">Renk teması seçin</option>
                  {hennaDressOptions.colorThemes.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Kaftan Uzunluğu</Label>
                <Select
                  value={formData.hennaDressDetails?.kaftanLength || ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    hennaDressDetails: { 
                      ...prev.hennaDressDetails, 
                      embroideryType: prev.hennaDressDetails?.embroideryType || '',
                      accessorySet: prev.hennaDressDetails?.accessorySet || '',
                      colorTheme: prev.hennaDressDetails?.colorTheme || '',
                      kaftanLength: e.target.value
                    } 
                  }))}
                  aria-label="Kaftan uzunluğu seçin"
                  title="Kaftan uzunluğu seçin"
                >
                  <option value="">Kaftan uzunluğu seçin</option>
                  {hennaDressOptions.kaftanLengths.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
          )}

          <FormGroup>
            <Label>Kısa Açıklama</Label>
            <TextArea
              value={formData.shortDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, shortDescription: e.target.value }))}
              placeholder="Ürünün kısa açıklamasını girin"
            />
          </FormGroup>

          <FormGroup>
            <Label>Detaylı Açıklama</Label>
            <TextArea
              value={formData.detailedDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, detailedDescription: e.target.value }))}
              placeholder="Ürünün detaylı açıklamasını girin"
              style={{ minHeight: '120px' }}
            />
          </FormGroup>

          <FormGroup>
            <Label>Ürün Fotoğrafları</Label>
            
            {/* File Upload */}
            <UrlInputContainer style={{ marginBottom: '15px' }}>
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploadingImage}
                style={{ 
                  padding: '10px',
                  border: '2px dashed #ddd',
                  borderRadius: '8px',
                  cursor: uploadingImage ? 'not-allowed' : 'pointer',
                  opacity: uploadingImage ? 0.6 : 1
                }}
              />
              {uploadingImage && (
                <span style={{ marginLeft: '10px', color: '#666' }}>
                  Sıkıştırılıyor...
                </span>
              )}
            </UrlInputContainer>

            {/* URL Input */}
            <Label style={{ marginTop: '20px', marginBottom: '10px' }}>Veya URL ile Ekle</Label>
            <UrlInputContainer>
              <UrlInput
                type="url"
                value={imageUrlInput}
                onChange={(e) => setImageUrlInput(e.target.value)}
                placeholder="Fotoğraf URL'si girin (örn: https://example.com/image.jpg)"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddImageUrl();
                  }
                }}
              />
              <Button $variant="primary" type="button" onClick={handleAddImageUrl}>
                <Plus size={16} />
                Ekle
              </Button>
            </UrlInputContainer>
            <UrlInfoText>
              <strong>📤 Dosya Yükleme:</strong> Resimler otomatik olarak WebP formatına dönüştürülür ve sıkıştırılır (max 500KB, 1920px, %75 kalite). 
              Orijinal 10MB JPEG resim → 200-300KB WebP'ye düşer, kalite kaybı gözle görülmez.
              <br /><strong>⚠️ ÖNEMLİ:</strong> React SPA'da server-side optimization yok. S3'e yüklenen resimler direkt kullanıcıya gönderilir. Bu yüzden yükleme sırasında optimize etmek kritik!
              <br /><br />
              <strong>🔗 URL ile Ekleme:</strong> Google Drive için:
              <br />1. Lütfen klasör linki DEĞİL, dosya linkini kullanın. 
              <br />2. Klasör içindeki dosyaya sağ tıklayıp "Bağlantıyı al" seçeneğinden dosya linkini kopyalayın.
              <br />3. <strong>ÖNEMLİ:</strong> Dosyanın paylaşım ayarlarını kontrol edin! Dosyaya sağ tıklayıp "Paylaş" → "Ayarlar değiştir" → "Herkesin bu dosyayı görüntülemesine izin ver" seçeneğini açın.
              <br />Format: <code>https://drive.google.com/file/d/FILE_ID/view?usp=sharing</code>
            </UrlInfoText>
            
            {formData.images.length > 0 && (
              <ImagePreviewContainer>
                {formData.images.map((image, index) => (
                  <ImagePreview key={index} $bgImage={image}>
                    <RemoveImageButton onClick={() => removeImage(index)}>
                      ×
                    </RemoveImageButton>
                  </ImagePreview>
                ))}
              </ImagePreviewContainer>
            )}
          </FormGroup>

          <ModalActions>
            <Button onClick={() => setIsModalOpen(false)}>
              <X size={16} />
              İptal
            </Button>
            <Button $variant="primary" onClick={handleSaveProduct}>
              <Save size={16} />
              Kaydet
            </Button>
          </ModalActions>
        </ModalContent>
      </Modal>
    </ProductManagementContainer>
  );
};

export default ProductManagement;
